import { AdjustmentsHorizontalIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useChat } from "../../../../libs/hooks";
import { useAuthoritive } from "../../../../libs/hooks/useAuthoritive";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import { setBuckets, updateBucket } from "../../../../redux/features/buckets/BucketsSlice";
import { editConversationScope, hideIntelligentFilteringMessage } from "../../../../redux/features/conversations/conversationsSlice";
import { ShineBorder } from "../../../Effects/ShineBorder";

export const IntelligentFilteringMessage: React.FC = () => {
    const dispatch = useAppDispatch();
    const selectedId = useAppSelector((state: RootState) => state.conversations.selectedId);
    const { Buckets } = useAppSelector((state) => state.buckets);
    const { conversations } = useAppSelector((state: RootState) => state.conversations);
    const chat = useChat();
    const authoritive = useAuthoritive();

    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const emailAddress = activeUserInfo?.email ?? '';
    const user = emailAddress;

    const suggestedBucket = Buckets.find((b) => b.id === conversations[selectedId].intelligentFilteringBucketId);

    if (!suggestedBucket) {
        return null;
    }

    const handleDismiss = () => {
        dispatch(hideIntelligentFilteringMessage({ chatId: selectedId }));
    };

    const handleChangeScope = async () => {
        dispatch(hideIntelligentFilteringMessage({ chatId: selectedId }));
        const chatState = conversations[selectedId];
        const authoritiveDocuments = Buckets.filter((b) => b.parent?.id === suggestedBucket.id).map((b) => b.id);
        const authScopeName = await authoritive.getAuthDocsScopeName(user);

        if (!authScopeName) {
            console.error('No AuthDocs scope found');
            return;
        }

        await chat
            .editChat(
                chatState.id,
                chatState.title,
                chatState.memoryBalance,
                authScopeName,
                chatState.selectedSites,
                chatState.hidden,
                authoritiveDocuments,
            )
            .then(() => {
                localStorage.setItem('lastSelectedChatScope', "AuthDocs:index-pcgpt-eya-chunks-prod");
                dispatch(setBuckets(Buckets.map((b) => ({ ...b, selected: false }))));
                dispatch(updateBucket({ ...suggestedBucket, selected: true }));
                dispatch(editConversationScope({ id: selectedId, newScope: authScopeName }));
            }).catch((error: Error | unknown) => {
                const errorMessage = `Unable to change chat scope. Details: ${
                    error instanceof Error ? error.message : String(error)
                }`;
                console.error(errorMessage);
            });
    };

    return (
        <div className="flex justify-center bg-slate-100 p-2 bg-slate-200 leading-6 shadow-md dark:bg-slate-900">
            <ShineBorder className="w-full mx-4 relative" color={'#E44EE9'} borderWidth={2}>
                <div className="flex flex-col items-start justify-center w-full h-full p-6 text-gray-500 bg-slate-50 dark:bg-slate-800 dark:text-slate-300 rounded-lg">
                    <button
                        className="absolute top-2 right-2 p-2 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-gray-400 hover:text-primary-500"
                        title="Dismiss"
                        aria-label="Dismiss"
                        onClick={handleDismiss}
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                    <p className="text-2xl font-bold mb-3">Intelligent Filtering</p>
                    <p className="text-base mb-4">
                        For best accuracy, authoritative '{suggestedBucket.title}' documents were used to answer your question. 
                        Click 'Keep Scope' if you intend to ask similar follow-up questions.
                    </p>
                    <button
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-300 hover:bg-primary-700 text-slate-50 transition-colors"
                        title="Keep scope"
                        aria-label="Keep scope"
                        onClick={handleChangeScope}
                    >
                        <AdjustmentsHorizontalIcon className="h-5 w-5" />
                        <span className="text-sm font-medium">Keep scope</span>
                    </button>
                </div>
            </ShineBorder>
        </div>
    );
};
